import { type IntlShape } from 'react-intl-next';

import { getAnonymousAvatarUrl, getAnonymousName } from '@canvas/anonymous-assets';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getCollaboratorTokenColor } from '@canvas/constants/get-color-value';
import { type Collaborator, type Collaborators } from '@canvas/frame-api-types';
import {
  type AiAgentIdentity,
  type Anonymous,
  BACKEND_ROVO_AGENT_PRESENCE_KIND,
  EXTERNAL_AGENT_PRESENCE_KIND,
  type Identity,
  IdentityKind,
  ROVO_AGENT_PRESENCE_KIND
} from '@canvas/model/identity';
import { type ClientId } from '@canvas/model/telepointers';

import { COLORS_TO_OMIT } from './constants.ts';
import { messages } from './i18n.ts';

export enum IdentityOwner {
  you = 'yourName',
  someoneElse = 'collaboratorName'
}

const getNicknameFromIdentity = (
  identity: Identity,
  clientId: ClientId,
  kind: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
): string => {
  if (identity.type === IdentityKind.AnonymousId) {
    return formatMessage(getAnonymousName(clientId)[kind]);
  }

  if (identity.type === IdentityKind.AiAgent) {
    return (
      identity.displayName ?? formatMessage(messages['presence.rovo-name.non-final'])
    );
  }

  return identity.displayName ?? '';
};

const identityToPartialCollaborator = (
  identity: Identity,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage'],
  agentPresentation?: AgentPresentation
) => {
  const color = getCollaboratorTokenColor(clientId, COLORS_TO_OMIT);
  const name = getNicknameFromIdentity(identity, clientId, ownerType, formatMessage);

  return {
    userId: agentPresentation?.userId ?? identity.accountId,
    clientId,
    color,
    name,
    ...(agentPresentation &&
    identity.type === IdentityKind.AiAgent &&
    identity.presenceKind !== ROVO_AGENT_PRESENCE_KIND
      ? { followCursor: false }
      : {})
  } satisfies Partial<Collaborator>;
};

const getAgentPresentation = (identity: AiAgentIdentity) =>
  ({
    avatarSrc: identity.picture,
    kind:
      identity.presenceKind === EXTERNAL_AGENT_PRESENCE_KIND
        ? EXTERNAL_AGENT_PRESENCE_KIND
        : ROVO_AGENT_PRESENCE_KIND,
    userId:
      identity.presenceKind !== ROVO_AGENT_PRESENCE_KIND &&
      identity.agentOwnerAccountId !== undefined
        ? `${identity.accountId}:${identity.agentOwnerAccountId}:${identity.agentType ?? 'unknown'}`
        : identity.accountId
  }) satisfies AgentPresentation;

export const authenticatedIdentityToCollaborator = (
  identity: Exclude<Identity, Anonymous>,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
) => {
  const agentPresentation =
    identity.type === IdentityKind.AiAgent &&
    isFeatureFlagEnabled('canvasAgentPresenceFacepileFrontend')
      ? getAgentPresentation(identity)
      : undefined;
  const externalAgentIdentity =
    agentPresentation?.kind === EXTERNAL_AGENT_PRESENCE_KIND &&
    identity.type === IdentityKind.AiAgent &&
    identity.presenceKind === EXTERNAL_AGENT_PRESENCE_KIND
      ? identity
      : undefined;
  const backendAgentIdentity =
    agentPresentation &&
    identity.type === IdentityKind.AiAgent &&
    identity.presenceKind !== ROVO_AGENT_PRESENCE_KIND
      ? identity
      : undefined;

  return {
    ...identityToPartialCollaborator(
      identity,
      clientId,
      ownerType,
      formatMessage,
      agentPresentation
    ),
    ...(agentPresentation ? { kind: agentPresentation.kind } : {}),
    ...(agentPresentation && identity.type === IdentityKind.AiAgent
      ? {
          agentId:
            identity.agentId?.replace(/^agent:/, '') ??
            (identity.presenceKind === BACKEND_ROVO_AGENT_PRESENCE_KIND
              ? identity.agentType
              : undefined),
          agentType: identity.agentType
        }
      : {}),
    ...(externalAgentIdentity ? { name: '' } : {}),
    ...(backendAgentIdentity?.agentOwnerAccountId !== undefined
      ? { agentOwnerName: backendAgentIdentity.agentOwnerName }
      : {}),
    avatar: { src: agentPresentation?.avatarSrc ?? identity.picture }
  } satisfies Collaborator;
};

export const anonymousIdentityToCollaborator = async (
  identity: Anonymous,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
): Promise<Collaborator> => {
  const svg = await getAnonymousAvatarUrl(clientId);

  return {
    ...identityToPartialCollaborator(identity, clientId, ownerType, formatMessage),
    avatar: {
      src: svg
    }
  };
};

export const userIdentityToCollaborator = async (
  identity: Identity,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
) =>
  identity.type === IdentityKind.AnonymousId
    ? await anonymousIdentityToCollaborator(identity, clientId, ownerType, formatMessage)
    : authenticatedIdentityToCollaborator(identity, clientId, ownerType, formatMessage);

export const participantsToCollaborators = async (
  currentUser: Identity,
  currentUserClientId: ClientId,
  participants: Record<number, Identity>,
  formatMessage: IntlShape['formatMessage']
): Promise<Collaborators> => {
  const me = await userIdentityToCollaborator(
    currentUser,
    currentUserClientId,
    IdentityOwner.you,
    formatMessage
  );

  const collaborators: Collaborator[] = await Promise.all(
    Object.entries(participants).map(([clientId, participant]) => {
      return userIdentityToCollaborator(
        participant,
        Number.parseInt(clientId),
        IdentityOwner.someoneElse,
        formatMessage
      );
    })
  );

  return { collaborators, me };
};
interface AgentPresentation {
  readonly avatarSrc: string | undefined;
  readonly kind: 'rovo-agent' | 'external-agent';
  readonly userId: string;
}
